import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield,
  Lock,
  Scan,
  AlertTriangle,
  Globe,
  Zap,
  CheckCircle2,
  ArrowRight,
  Cookie,
  Clock,
  X,
  Menu,
} from "lucide-react";
import { motion } from "framer-motion";

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);

  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  return w;
}

// ─────────────────────────────────────────────────────────────────────────────
// Social Icons
// ─────────────────────────────────────────────────────────────────────────────
const LinkedInIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-label="LinkedIn"
  >
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037
      -1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046
      c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286z
      M5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063
      2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065z
      M7.119 20.452H3.555V9h3.564v11.452z
      M22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24
      h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const XIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-label="X (Twitter)"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17
      l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08
      l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126
      H5.117L17.083 19.77z" />
  </svg>
);

const MailIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-label="Email"
  >
    <path d="M20 4H4c-1.103 0-2 .897-2 2v12c0 1.103.897 2 2 2h16
      c1.103 0 2-.897 2-2V6c0-1.103-.897-2-2-2zm0 2v.511l-8 5.018
      -8-5.018V6h16zM4 18V9.044l7.386 4.633a1 1 0 0 0 1.228 0
      L20 9.044 20.002 18H4z" />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// Data
// ─────────────────────────────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: <Lock className="w-7 h-7" />,
    title: "SSL/HTTPS Certificate",
    description:
      "Validates certificate chain, expiry, issuer and TLS protocol version",
    color: "#00F0FF",
  },
  {
    icon: <Shield className="w-7 h-7" />,
    title: "Security Headers",
    description:
      "Checks CSP, HSTS, X-Frame-Options, Referrer-Policy and more",
    color: "#7000FF",
  },
  {
    icon: <Scan className="w-7 h-7" />,
    title: "Open Port Scan",
    description: "Identifies unexpected open ports and high-risk services",
    color: "#FF0055",
  },
  {
    icon: <Globe className="w-7 h-7" />,
    title: "DNS Configuration",
    description:
      "Validates A/AAAA records, SPF, DMARC and MX configuration",
    color: "#00FF94",
  },
  {
    icon: <Zap className="w-7 h-7" />,
    title: "Technology Stack",
    description:
      "Detects frameworks, servers and checks for version info leaks",
    color: "#FFD600",
  },
  {
    icon: <AlertTriangle className="w-7 h-7" />,
    title: "Malware / Blacklist",
    description:
      "Scans for blacklist entries, suspicious redirects and malicious content",
    color: "#FF0055",
  },
  {
    icon: <Clock className="w-7 h-7" />,
    title: "Response Performance",
    description: "Averages response time with min/max variance analysis",
    color: "#00F0FF",
  },
  {
    icon: <Cookie className="w-7 h-7" />,
    title: "Cookie Security",
    description:
      "Checks Secure flag, HttpOnly, SameSite attributes on all cookies",
    color: "#7000FF",
  },
];

const STATS = [
  { value: "100+", label: "Websites Scanned" },
  { value: "8",    label: "Security Checks"  },
  { value: "99.9%",label: "Accuracy Rate"    },
  { value: "<60s", label: "Scan Duration"    },
];

const STEPS = [
  { step: "01", title: "Enter Details", desc: "Provide your website URL and email address" },
  { step: "02", title: "Pay ₹299",      desc: "Secure one-time payment via Razorpay"       },
  { step: "03", title: "Get Report",    desc: "Professional PDF delivered to your inbox"   },
];

const TRUST_BADGES = [
  { label: "SSL Encrypted",    color: "#00FF94" },
  { label: "No Data Stored",   color: "#00F0FF" },
  { label: "Razorpay Secured", color: "#7000FF" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const LandingPage = () => {
  const navigate   = useNavigate();
  const width      = useWindowWidth();
  const isMobile   = width < 640;
  const isTablet   = width < 1024;
  const [menuOpen, setMenuOpen] = useState(false);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const footerLinks = [
    {
      heading: "Platform",
      links: [
        { label: "Security Scanner", action: () => navigate("/scanner") },
        {
          label: "Pricing",
          action: () =>
            document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" }),
        },
      ],
    },
    {
      heading: "Resources",
      links: [
        { label: "Privacy Policy",   action: () => navigate("/privacy") },
        { label: "Terms of Service", action: () => navigate("/terms")   },
        { label: "Blog",             action: () => navigate("/blog")    },
      ],
    },
    {
      heading: "Contact",
      links: [
        {
          label: "webshieldapp@gmail.com",
          href:  "mailto:webshieldapp@gmail.com",
          icon:  <MailIcon size={13} />,
        },
      ],
    },
  ];

  // ── Shared style helpers ───────────────────────────────────────────────────
  const sectionPadH = isMobile ? 16 : 24;

  return (
    <div className="min-h-screen bg-background">

      {/* ── Navbar ──────────────────────────────────────────────────────────── */}
      <nav
        style={{
          position:       "sticky",
          top:            0,
          zIndex:         50,
          background:     "rgba(2,2,16,0.95)",
          backdropFilter: "blur(12px)",
          borderBottom:   "1px solid rgba(112,0,255,0.3)",
          padding:        isMobile ? "0 16px" : "0 32px",
          height:         60,
          display:        "flex",
          alignItems:     "center",
          justifyContent: "space-between",
        }}
      >
        {/* Brand */}
        <div
          onClick={() => navigate("/")}
          style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
        >
          <Shield size={24} color="#00F0FF" />
          <span style={{ fontWeight: 800, fontSize: isMobile ? 17 : 20, color: "#fff" }}>
            Web<span style={{ color: "#00F0FF" }}>Shield</span>
          </span>
        </div>

        {/* Desktop nav links */}
        {!isMobile && (
          <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
            {["features", "how-it-works", "pricing"].map((id) => (
              <span
                key={id}
                onClick={() => scrollTo(id)}
                style={{
                  color:          "#B0B8D0",
                  fontSize:       14,
                  cursor:         "pointer",
                  fontWeight:     500,
                  textTransform:  "capitalize",
                }}
              >
                {id.replace("-", " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              </span>
            ))}
            <button
              onClick={() => navigate("/scanner")}
              style={{
                background:   "linear-gradient(135deg,#7000FF,#00F0FF)",
                border:       "none",
                borderRadius: 8,
                padding:      "8px 20px",
                color:        "#fff",
                fontWeight:   700,
                fontSize:     13,
                cursor:       "pointer",
              }}
            >
              Start Scan →
            </button>
          </div>
        )}

        {/* Mobile hamburger */}
        {isMobile && (
          <button
            onClick={() => setMenuOpen((o) => !o)}
            style={{
              background: "transparent",
              border:     "none",
              color:      "#fff",
              cursor:     "pointer",
              padding:    4,
            }}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        )}
      </nav>

      {/* Mobile dropdown */}
      {isMobile && menuOpen && (
        <div
          style={{
            position:       "fixed",
            top:            60,
            left:           0,
            right:          0,
            zIndex:         49,
            background:     "rgba(2,2,16,0.98)",
            borderBottom:   "1px solid rgba(112,0,255,0.3)",
            padding:        "16px 20px",
            display:        "flex",
            flexDirection:  "column",
            gap:            12,
          }}
        >
          {["features", "how-it-works", "pricing"].map((id) => (
            <span
              key={id}
              onClick={() => scrollTo(id)}
              style={{
                color:        "#B0B8D0",
                fontSize:     15,
                cursor:       "pointer",
                padding:      "8px 0",
                borderBottom: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              {id.replace("-", " ").replace(/\b\w/g, (c) => c.toUpperCase())}
            </span>
          ))}
          <button
            onClick={() => { navigate("/scanner"); setMenuOpen(false); }}
            style={{
              background:   "linear-gradient(135deg,#7000FF,#00F0FF)",
              border:       "none",
              borderRadius: 8,
              padding:      "12px",
              color:        "#fff",
              fontWeight:   700,
              fontSize:     15,
              cursor:       "pointer",
              marginTop:    4,
            }}
          >
            Start Scan →
          </button>
        </div>
      )}

      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <section
        style={{
          position:       "relative",
          minHeight:      isMobile ? "85vh" : "92vh",
          display:        "flex",
          alignItems:     "center",
          justifyContent: "center",
          overflow:       "hidden",
        }}
      >
        {/* Background image */}
        <div
          style={{
            position:           "absolute",
            inset:              0,
            zIndex:             0,
            backgroundImage:    "url(https://images.unsplash.com/photo-1550275910-23089007c6f4)",
            backgroundSize:     "cover",
            backgroundPosition: "center",
            opacity:            0.08,
          }}
        />

        {/* Radial glow */}
        <div
          style={{
            position:   "absolute",
            inset:      0,
            zIndex:     0,
            background: "radial-gradient(circle at 50% 50%, rgba(0,240,255,0.10) 0%, transparent 65%)",
          }}
        />

        {/* Content */}
        <div
          style={{
            position: "relative",
            zIndex:   10,
            width:    "100%",
            maxWidth: 860,
            margin:   "0 auto",
            padding:  isMobile ? "0 16px" : isTablet ? "0 32px" : "0 24px",
            textAlign:"center",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge */}
            <div
              style={{
                display:      "inline-block",
                background:   "rgba(112,0,255,0.2)",
                border:       "1px solid rgba(112,0,255,0.5)",
                borderRadius: 20,
                padding:      "5px 16px",
                fontSize:     isMobile ? 11 : 12,
                color:        "#fff",
                marginBottom: 16,
                fontWeight:   600,
              }}
            >
              Professional Website Security Analysis
            </div>

            {/* Headline */}
            <h1
              className="gradient-text font-heading font-black"
              style={{
                fontSize:     isMobile ? "clamp(36px,12vw,56px)" : "clamp(40px,8vw,80px)",
                lineHeight:   1.1,
                marginBottom: 12,
              }}
            >
              WebShield
            </h1>

            <p
              className="font-heading"
              style={{
                fontSize:     isMobile ? "clamp(18px,6vw,28px)" : "clamp(22px,4vw,40px)",
                fontWeight:   300,
                color:        "#fff",
                marginBottom: 10,
              }}
            >
              Secure Everything.
            </p>

            <p
              style={{
                fontSize:     isMobile ? 14 : isTablet ? 15 : 17,
                color:        "#D0D8F0",
                maxWidth:     600,
                margin:       "0 auto 32px",
                lineHeight:   1.7,
              }}
            >
              8-protocol security scan in under 60 seconds. Identify
              vulnerabilities, get a professional PDF report, protect your
              digital assets.
            </p>

            {/* CTA */}
            <button
              onClick={() => navigate("/scanner")}
              className="btn-primary"
              style={{
                fontSize:       isMobile ? 15 : 17,
                padding:        isMobile ? "12px 28px" : "14px 36px",
                display:        "inline-flex",
                alignItems:     "center",
                gap:            10,
                width:          isMobile ? "100%" : "auto",
                justifyContent: "center",
              }}
            >
              Start Security Scan <ArrowRight size={20} />
            </button>

            <p style={{ marginTop: 12, color: "#7A8AB0", fontSize: 13 }}>
              Only{" "}
              <span style={{ color: "#00FF94", fontWeight: 700 }}>₹299</span>
              {" "}per scan · Instant PDF report
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            style={{
              display:             "grid",
              gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
              gap:                 isMobile ? 10 : 16,
              marginTop:           isMobile ? 40 : 64,
            }}
          >
            {STATS.map((s, i) => (
              <div
                key={i}
                className="glass-card"
                style={{ padding: isMobile ? "16px 10px" : "20px 16px", textAlign: "center" }}
              >
                <div
                  style={{
                    fontSize:   isMobile ? 26 : 34,
                    fontWeight: 800,
                    color:      "#00F0FF",
                    fontFamily: "var(--font-heading)",
                  }}
                >
                  {s.value}
                </div>
                <div style={{ fontSize: 11, color: "#C0C8E0", marginTop: 4, fontWeight: 500 }}>
                  {s.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────────────────── */}
      <section
        id="features"
        style={{
          padding: isMobile
            ? "60px 16px"
            : isTablet
              ? "72px 24px"
              : "96px 24px",
        }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            style={{ textAlign: "center", marginBottom: isMobile ? 36 : 56 }}
          >
            <h2
              className="font-heading"
              style={{
                fontSize:     isMobile ? "clamp(22px,6vw,36px)" : "clamp(28px,5vw,48px)",
                fontWeight:   700,
                marginBottom: 12,
                color:        "#fff",
              }}
            >
              8 Comprehensive Security Checks
            </h2>
            <p style={{ fontSize: isMobile ? 14 : isTablet ? 15 : 17, color: "#C0C8E0", maxWidth: 560, margin: "0 auto" }}>
              Every scan performs a deep analysis across multiple security dimensions
            </p>
          </motion.div>

          <div
            style={{
              display:             "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : isTablet
                  ? "repeat(2,1fr)"
                  : "repeat(4,1fr)",
              gap: isMobile ? 12 : 16,
            }}
          >
            {FEATURES.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: isMobile ? 0 : i * 0.07 }}
                viewport={{ once: true }}
                className="glass-card"
                style={{
                  padding:        isMobile ? "20px 18px" : "28px 24px",
                  borderTop:      `3px solid ${f.color}`,
                  display:        "flex",
                  alignItems:     isMobile ? "flex-start" : "initial",
                  gap:            isMobile ? 14 : 0,
                  flexDirection:  isMobile ? "row" : "column",
                }}
              >
                <div style={{ color: f.color, marginBottom: isMobile ? 0 : 14, flexShrink: 0 }}>
                  {f.icon}
                </div>
                <div>
                  <h3
                    className="font-heading"
                    style={{ fontSize: isMobile ? 14 : 15, fontWeight: 700, marginBottom: 6, color: "#fff" }}
                  >
                    {f.title}
                  </h3>
                  <p style={{ fontSize: 12, color: "#C0C8E0", lineHeight: 1.6, margin: 0 }}>
                    {f.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ────────────────────────────────────────────────────── */}
      <section
        id="how-it-works"
        style={{
          padding:      isMobile ? "56px 16px" : "80px 24px",
          background:   "rgba(13,13,43,0.5)",
          borderTop:    "1px solid rgba(26,26,62,1)",
          borderBottom: "1px solid rgba(26,26,62,1)",
        }}
      >
        <div style={{ maxWidth: 860, margin: "0 auto", textAlign: "center" }}>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <h2
              className="font-heading"
              style={{
                fontSize:     isMobile ? "clamp(22px,6vw,36px)" : "clamp(26px,4vw,44px)",
                fontWeight:   700,
                marginBottom: 8,
                color:        "#fff",
              }}
            >
              How It Works
            </h2>
            <p style={{ color: "#C0C8E0", marginBottom: isMobile ? 36 : 56, fontSize: isMobile ? 14 : 16 }}>
              Three simple steps to full security clarity
            </p>
          </motion.div>

          <div
            style={{
              display:             "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)",
              gap:                 isMobile ? 24 : 32,
            }}
          >
            {STEPS.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                viewport={{ once: true }}
                style={{
                  display:    isMobile ? "flex" : "block",
                  gap:        16,
                  alignItems: "flex-start",
                  textAlign:  isMobile ? "left" : "center",
                }}
              >
                {/* Step circle */}
                <div
                  style={{
                    width:          isMobile ? 48 : 60,
                    height:         isMobile ? 48 : 60,
                    borderRadius:   "50%",
                    flexShrink:     0,
                    background:     "linear-gradient(135deg,#7000FF22,#00F0FF22)",
                    border:         "2px solid rgba(0,240,255,0.3)",
                    display:        "flex",
                    alignItems:     "center",
                    justifyContent: "center",
                    margin:         isMobile ? "0" : "0 auto 16px",
                    fontSize:       isMobile ? 16 : 20,
                    fontWeight:     800,
                    color:          "#00F0FF",
                    fontFamily:     "var(--font-heading)",
                  }}
                >
                  {item.step}
                </div>

                <div>
                  <h3
                    className="font-heading"
                    style={{ fontSize: isMobile ? 16 : 20, fontWeight: 700, marginBottom: 6, color: "#fff" }}
                  >
                    {item.title}
                  </h3>
                  <p style={{ color: "#C0C8E0", fontSize: 13, lineHeight: 1.6, margin: 0 }}>
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing / CTA ───────────────────────────────────────────────────── */}
      <section
        id="pricing"
        style={{ padding: isMobile ? "56px 16px" : "96px 24px" }}
      >
        <div style={{ maxWidth: 700, margin: "0 auto", textAlign: "center" }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="glass-card"
            style={{ padding: isMobile ? "36px 20px" : "56px 48px", borderTop: "3px solid #7000FF" }}
          >
            <Shield
              size={isMobile ? 42 : 52}
              color="#00F0FF"
              style={{ margin: "0 auto 16px" }}
            />
            <h2
              className="font-heading"
              style={{ fontSize: isMobile ? 24 : 36, fontWeight: 700, marginBottom: 12, color: "#fff" }}
            >
              Ready to Secure Your Website?
            </h2>
            <p style={{ color: "#C0C8E0", fontSize: isMobile ? 14 : 16, marginBottom: 32, lineHeight: 1.7 }}>
              Join website owners protecting their digital presence with a single scan
            </p>
            <button
              onClick={() => navigate("/scanner")}
              className="btn-primary"
              style={{
                fontSize:       isMobile ? 15 : 16,
                padding:        isMobile ? "13px 28px" : "14px 36px",
                display:        "inline-flex",
                alignItems:     "center",
                gap:            10,
                width:          isMobile ? "100%" : "auto",
                justifyContent: "center",
              }}
            >
              Start Your First Scan <ArrowRight size={18} />
            </button>
            <p style={{ marginTop: 14, color: "#a2a7b0", fontSize: 13 }}>
              ₹299 · One-time · Instant PDF · Email delivery
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      <footer style={{ background: "rgba(5,5,20,0.97)", borderTop: "1px solid rgba(42,42,74,0.8)" }}>
        <div
          style={{
            maxWidth:            1100,
            margin:              "0 auto",
            padding:             isMobile
              ? "36px 16px 28px"
              : isTablet
                ? "44px 20px 32px"
                : "56px 32px 40px",
            display:             "grid",
            gridTemplateColumns: isMobile
              ? "1fr 1fr"
              : isTablet
                ? "repeat(2, 1fr)"
                : "2fr 1fr 1fr 1fr 1fr",
            gap: isMobile ? 28 : 40,
          }}
        >
          {/* Brand column */}
          <div style={{ gridColumn: isMobile ? "span 2" : "auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <Shield size={24} color="#00F0FF" />
              <span style={{ fontWeight: 800, fontSize: 20, color: "#fff" }}>
                Web<span style={{ color: "#00F0FF" }}>Shield</span>
              </span>
            </div>

            <p style={{ color: "#8898B0", fontSize: 13, lineHeight: 1.7, marginBottom: 20, maxWidth: 240 }}>
              Professional website security scanning. Protect your digital assets
              with instant, comprehensive reports.
            </p>

            {/* Social icons */}
            <div style={{ display: "flex", gap: 10 }}>
              {/* LinkedIn */}
              <a
                href="https://linkedin.com/company/webshield"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  width:          36,
                  height:         36,
                  borderRadius:   8,
                  background:     "rgba(42,42,74,0.6)",
                  border:         "1px solid rgba(42,42,74,1)",
                  display:        "inline-flex",
                  alignItems:     "center",
                  justifyContent: "center",
                  color:          "#0A66C2",
                  textDecoration: "none",
                  transition:     "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background  = "#0A66C2";
                  e.currentTarget.style.color       = "#fff";
                  e.currentTarget.style.transform   = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background  = "rgba(42,42,74,0.6)";
                  e.currentTarget.style.color       = "#0A66C2";
                  e.currentTarget.style.transform   = "translateY(0)";
                }}
              >
                <LinkedInIcon size={17} />
              </a>

              {/* X (Twitter) */}
              <a
                href="https://x.com/WebshieldApp"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  width:          36,
                  height:         36,
                  borderRadius:   8,
                  background:     "rgba(42,42,74,0.6)",
                  border:         "1px solid rgba(42,42,74,1)",
                  display:        "inline-flex",
                  alignItems:     "center",
                  justifyContent: "center",
                  color:          "#fff",
                  textDecoration: "none",
                  transition:     "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background  = "#fff";
                  e.currentTarget.style.color       = "#000";
                  e.currentTarget.style.transform   = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background  = "rgba(42,42,74,0.6)";
                  e.currentTarget.style.color       = "#fff";
                  e.currentTarget.style.transform   = "translateY(0)";
                }}
              >
                <XIcon size={15} />
              </a>
            </div>
          </div>

          {/* Link columns */}
          {footerLinks.map((col, ci) => (
            <div key={ci}>
              <p style={{ color: "#fff", fontWeight: 700, fontSize: 13, marginBottom: 14, letterSpacing: 0.5 }}>
                {col.heading}
              </p>
              {col.links.map((link, li) =>
                link.href ? (
                  // ── mailto: anchor — no JS, opens mail client directly ──
                  <p key={li} style={{ marginBottom: 9 }}>
                    <a
                      href={link.href}
                      style={{
                        color:          "#8898B0",
                        fontSize:       13,
                        cursor:         "pointer",
                        textDecoration: "none",
                        display:        "inline-flex",
                        alignItems:     "center",
                        gap:            6,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = "#00F0FF";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = "#8898B0";
                      }}
                    >
                      {link.icon && (
                        <span style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                          {link.icon}
                        </span>
                      )}
                      {link.label}
                    </a>
                  </p>
                ) : (
                  // ── internal navigate / scroll ──
                  <p key={li} style={{ marginBottom: 9 }}>
                    <span
                      style={{ color: "#8898B0", fontSize: 13, cursor: "pointer" }}
                      onClick={link.action}
                      onMouseEnter={(e) => (e.target.style.color = "#00F0FF")}
                      onMouseLeave={(e) => (e.target.style.color = "#8898B0")}
                    >
                      {link.label}
                    </span>
                  </p>
                )
              )}
            </div>
          ))}
        </div>

        {/* Divider */}
        <div style={{ borderTop: "1px solid rgba(42,42,74,0.6)", margin: "0 16px" }} />

        {/* Bottom bar */}
        <div
          style={{
            maxWidth:       1100,
            margin:         "0 auto",
            padding:        isMobile ? "16px" : isTablet ? "16px 20px" : "20px 32px",
            display:        "flex",
            flexDirection:  isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems:     isMobile ? "flex-start" : "center",
            gap:            12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Shield size={14} color="#7000FF" />
            <span style={{ color: "#6A7A9A", fontSize: 12 }}>
              © {new Date().getFullYear()} WebShield. All rights reserved.
            </span>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: isMobile ? 12 : 20 }}>
            {TRUST_BADGES.map((badge, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <CheckCircle2 size={12} color={badge.color} />
                <span style={{ color: "#8898B0", fontSize: 11, fontWeight: 600 }}>
                  {badge.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;